"use client"

import { JSX, useState } from "react"
import { toast } from "sonner"
import { CircleX, Loader2 } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { cancelJob } from "@/services/hpcJobsService"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

/** The type of entity being cancelled, controls the confirmation copy. */
type CancelEntityType = "job" | "workflow" | "arrayJob"

/** Confirmation copy and button labels for each cancellable entity type. */
const ENTITY_COPY: Record<
  CancelEntityType,
  { action: string; title: string; describe: (name: string) => React.ReactNode; toast: (name: string) => string }
> = {
  job: {
    action: "Cancel job",
    title: "Cancel this job?",
    describe: (name) => (
      <>
        This will cancel job <strong>{name}</strong>. This action cannot be undone.
      </>
    ),
    toast: (name) => `Cancellation requested for job "${name}"`,
  },
  workflow: {
    action: "Cancel workflow",
    title: "Cancel this workflow?",
    describe: (name) => (
      <>
        This will cancel every job in workflow <strong>{name}</strong>. This action cannot be
        undone.
      </>
    ),
    toast: (name) => `Cancellation requested for workflow "${name}"`,
  },
  arrayJob: {
    action: "Cancel array job",
    title: "Cancel this array job?",
    describe: (name) => (
      <>
        This will cancel every child task in array job <strong>{name}</strong>. This action
        cannot be undone.
      </>
    ),
    toast: (name) => `Cancellation requested for array job "${name}"`,
  },
}

interface CancelJobButtonProps {
  /**
   * The identifier to cancel. For a job this is the AWS Batch job ID; for a
   * workflow this is the workflow ID (which cancels every job in it); for an
   * array job this is the parent job ID (which cancels every child task).
   */
  cancelId: string
  /** Human-readable name shown in the confirmation dialog. */
  name: string
  /** Whether this cancels a single job, an entire workflow, or an array job. */
  entityType: CancelEntityType
  /**
   * Render a labelled button instead of the default icon-only button. Use this
   * where the action needs to be self-explanatory rather than sitting in a
   * table row of icons.
   */
  showLabel?: boolean
  /**
   * Callback invoked after a cancel request succeeds, so the parent can
   * refresh its data to reflect the new job state.
   */
  onCancelled: () => void
}

/**
 * A destructive icon button that cancels an HPC job or workflow.
 *
 * Renders a circle-x icon that opens a confirmation dialog. On confirmation it
 * calls the cancel endpoint, shows a success or error toast, and notifies the
 * parent via onCancelled so the table can refresh. While the request is in
 * flight the icon shows a spinner and the action is disabled.
 *
 * @param props - The job/workflow identifier, display name, entity type, and
 *                the onCancelled refresh callback.
 * @returns The cancel icon button wrapped in a confirmation dialog.
 */
export function CancelJobButton({
  cancelId,
  name,
  entityType,
  showLabel = false,
  onCancelled,
}: CancelJobButtonProps): JSX.Element {
  const { getAccessToken } = useAuth()

  const copy = ENTITY_COPY[entityType]

  // Whether a cancel request is currently in flight.
  const [isCancelling, setIsCancelling] = useState<boolean>(false)
  // Controls the confirmation dialog open state so it can be closed on success.
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  /**
   * Performs the cancel request and surfaces the result via toasts.
   *
   * Prevents the AlertDialog from auto-closing until the request settles so
   * that a failure keeps the dialog open for a retry.
   *
   * @param event - The click event from the confirm button, whose default
   *                (closing the dialog) is prevented until the request settles.
   */
  const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    // Prevent the AlertDialogAction default behaviour of closing the dialog,
    // so it stays open until the async request completes.
    event.preventDefault()

    setIsCancelling(true)
    try {
      const token = await getAccessToken()
      await cancelJob(cancelId, token)
      toast.success(copy.toast(name))
      setIsDialogOpen(false)
      onCancelled()
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to cancel"
      toast.error(message)
    } finally {
      setIsCancelling(false)
    }
  }

  return (
    <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <AlertDialogTrigger asChild>
        {showLabel ? (
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
            <CircleX className="h-4 w-4" />
            {copy.action}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            title={copy.action}
          >
            <CircleX className="h-4 w-4" />
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription>{copy.describe(name)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isCancelling}>Keep running</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={handleConfirm}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Cancelling...
              </>
            ) : (
              "Cancel it"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
